/**
 * «Выплаты» фирмы — блок `WEB-2.3` (`FR-WEB-602`, решения формы `Б3`–`Б6`,
 * `Б10`, `Б12`–`Б17`, `Б20`): расчёты с водителями и по перегонам — список
 * страницами, отбор по получателю и работе, форма записи и правки, удаление.
 *
 * Экран показывает **каждую** выплату ответа в порядке ответа; отбор уходит
 * параметрами запроса (`Б15`), итогов нет (`Б4`, развилка Р7 А). «По
 * перегонам» — та же выплата с работой перегона из того же выбора работ
 * (развилка Р5): какие работы фирмы, решает сервер параметром фирмы, и экран
 * по рынку работы не ветвится.
 *
 * Получатель — членство фирмы, подписанное по ответу `listCompanyDrivers`
 * **тремя** исходами, а не двумя (`Б12`): имя из ответа; ответ пришёл, а
 * членства в нём нет — «не в списке водителей фирмы»; ответа нет —
 * «имя получателя недоступно». Пустой ответ и неизвестность — разные вещи, и
 * одно слово на два состояния солгало бы бухгалтеру. Выбор получателя —
 * ровно водители из того же ответа (`Б14`): списка членов экран не зовёт, он
 * бухгалтеру закрыт. Ответа нет — на месте выбора текст отказа сервера, а
 * отправку клиент не запрещает: пустой получатель уходит `null`, и годность
 * судит сервер.
 *
 * Тело записи и правки — `CompanyPayoutInput` целиком (`Б13`): шесть ключей,
 * незаполненное — `null`. Текущий получатель и текущая работа записи стоят в
 * выборе отдельными пунктами, даже когда их нет в списке водителей и на
 * загруженных страницах работ: правка суммы не меняет их молча.
 *
 * Кто вправе вести учёт, решает сервер: `403` и `404` списка — побайтово одно
 * «нет доступа» на месте экрана (`FR-WEB-006`); отказ действия — словами
 * сервера на месте действия, экран остаётся.
 */

import { ApiFailure, newIdempotencyKey } from '../api/client';
import type { ApiClient, CompanyPayoutSubmission, PayoutsQuery } from '../api/client';
import { nextPageCursor } from '../api/contract';
import type { CompanyDriver, CompanyPayout, Work } from '../api/contract';
import { CURRENCIES } from '../api/generated/enums';
import { activeLocale, currencyStringKey, workStatusStringKey } from '../i18n';
import type { Strings } from '../i18n';
import {
  createFieldMessageSlot,
  dateLabel,
  enumLabel,
  moneyLabel,
  placeState,
  refusalText,
  renderFieldMessages,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface PayoutsScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

export interface PayoutsScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  /** Выплаты в том порядке, в котором их вернул сервер, — все страницы подряд. */
  readonly payouts: readonly CompanyPayout[];
  load(): Promise<void>;
  /** Переподписать экран на новом языке, не трогая введённого (долг `TD-72`). */
  relabel(next: Strings): void;
}

/** Вариант выбора: значение провода и подпись. */
interface Choice {
  readonly value: string;
  readonly caption: string;
}

/** Перестроить варианты выбора, сохранив выбранное. */
function fillSelect(select: HTMLSelectElement, choices: readonly Choice[]): void {
  const chosen = select.value;
  const options: HTMLOptionElement[] = [];

  for (const choice of choices) {
    const option = document.createElement('option');

    option.value = choice.value;
    option.textContent = choice.caption;
    options.push(option);
  }

  select.replaceChildren(...options);
  select.value = chosen;

  if (select.value !== chosen) {
    select.selectedIndex = 0;
  }
}

/** Первые восемь знаков идентификатора — различитель нейтральной подписи. */
function shortId(id: string): string {
  return id.slice(0, 8);
}

/** Пустое поле — `null`: обязательность и годность значения судит сервер. */
function textOf(raw: string): string | null {
  return raw === '' ? null : raw;
}

/** Значение порождённого перечня, совпавшее с выбранным, либо `null`. */
function pick<T extends string>(values: readonly T[], chosen: string): T | null {
  for (const value of values) {
    if (value === chosen) {
      return value;
    }
  }

  return null;
}

/** Поле формы: подпись, элемент и место под сообщение сервера. */
function labelled(caption: HTMLElement, control: HTMLElement, ...extra: HTMLElement[]): HTMLLabelElement {
  const label = document.createElement('label');

  label.className = 'form-field';
  caption.className = 'form-field__caption';
  label.append(caption, control, ...extra, createFieldMessageSlot());

  return label;
}

/** Исход чтения водителей фирмы: ответа ещё нет, ответ пришёл, ответа нет. */
type DriversOutcome =
  | { readonly kind: 'pending' }
  | { readonly kind: 'answered'; readonly drivers: readonly CompanyDriver[] }
  | { readonly kind: 'refused'; readonly text: string };

/** Форма записи и правки: узлы живут, пока форма открыта (долг `TD-72`). */
interface PayoutForm {
  /** Выплата правки либо `null` — новая запись. */
  readonly editing: CompanyPayout | null;
  readonly form: HTMLFormElement;
  readonly recipient: HTMLSelectElement;
  readonly recipientRefusal: HTMLElement;
  readonly amount: HTMLInputElement;
  readonly currency: HTMLSelectElement;
  readonly paidOn: HTMLInputElement;
  readonly work: HTMLSelectElement;
  readonly worksMore: HTMLButtonElement;
  readonly comment: HTMLTextAreaElement;
  readonly submit: HTMLButtonElement;
  readonly close: HTMLButtonElement;
  readonly captions: Readonly<Record<'recipient' | 'amount' | 'currency' | 'paidOn' | 'work' | 'comment', HTMLElement>>;
}

export function createPayoutsScreen(options: PayoutsScreenOptions): PayoutsScreen {
  const { client, companyId } = options;
  let { strings } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const filter = document.createElement('div');
  const filterRecipientCaption = document.createElement('span');
  const filterRecipient = document.createElement('select');
  const filterWorkCaption = document.createElement('span');
  const filterWork = document.createElement('select');
  const filterReset = document.createElement('button');
  const createButton = document.createElement('button');
  const notice = document.createElement('p');
  const formNotice = document.createElement('p');
  const list = document.createElement('ul');
  const more = document.createElement('button');

  element.className = 'screen screen-payouts';
  filter.className = 'payout-filter';
  list.className = 'accounting-list';
  notice.className = 'notice';
  formNotice.className = 'notice';
  createButton.className = 'action';
  filterReset.className = 'action';
  more.className = 'action';
  createButton.type = 'button';
  filterReset.type = 'button';
  more.type = 'button';

  // Выборы отбора называются именами параметров запроса.
  filterRecipient.name = 'membership_id';
  filterWork.name = 'work_id';
  filter.append(
    labelled(filterRecipientCaption, filterRecipient),
    labelled(filterWorkCaption, filterWork),
    filterReset,
  );

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — та же величина, что на экране фирмы. */
  let answered = false;
  let payouts: CompanyPayout[] = [];
  let cursor: string | null = null;
  /** Поколение чтения списка: ответ устаревшего отбора к экрану не применяется. */
  let generation = 0;
  let loadingMore = false;

  let drivers: DriversOutcome = { kind: 'pending' };

  /** Работы фирмы с загруженных страниц `listWorks` — в порядке ответа. */
  let works: Work[] = [];
  let worksCursor: string | null = null;
  /** Ответила ли лента работ фирмы — до ответа `getWork` не зовётся (`Б10`). */
  let worksSettled = false;
  let worksLoading = false;
  /** Работы вне загруженных страниц: ответ `getWork` либо `null` — отказ. */
  const lookedUp = new Map<string, Work | null>();

  let form: PayoutForm | null = null;
  let sending = false;
  /** Ключ идемпотентности текущей отправки записи либо `null`. */
  let idempotencyKey: string | null = null;
  /** Выплаты, чьё удаление в пути: второй запрос не уходит. */
  const deleting = new Set<string>();

  /** Подписи экрана — одним местом: смена языка переподписывает их на месте. */
  function caption(): void {
    heading.textContent = strings.payouts_title;
    filterRecipientCaption.textContent = strings.payout_form_recipient;
    filterWorkCaption.textContent = strings.accounting_field_work;
    filterReset.textContent = strings.accounting_filter_reset;
    createButton.textContent = strings.payouts_create;
    more.textContent = strings.accounting_more;

    if (form !== null) {
      form.captions.recipient.textContent = strings.payout_form_recipient;
      form.captions.amount.textContent = strings.accounting_field_amount;
      form.captions.currency.textContent = strings.accounting_field_currency;
      form.captions.paidOn.textContent = strings.payout_form_paid_on;
      form.captions.work.textContent = strings.accounting_field_work;
      form.captions.comment.textContent = strings.accounting_field_comment;
      form.worksMore.textContent = strings.accounting_works_more;
      form.submit.textContent = strings.payout_form_submit;
      form.close.textContent = strings.accounting_form_close;
    }
  }

  /**
   * Подпись получателя — три исхода (`Б12`): имя из ответа водителей (или
   * «имя не указано»); ответ пришёл без этого членства; ответа нет. Пока
   * список водителей в пути, исхода ещё нет — своя подпись «загружается»,
   * а не «недоступно» (ревью `Р8`): «ещё нет» и «нет» — разные состояния.
   */
  function recipientCaption(membershipId: string): string {
    if (drivers.kind === 'refused') {
      return strings.payout_recipient_unavailable;
    }

    if (drivers.kind !== 'answered') {
      return strings.payout_recipient_loading;
    }

    for (const driver of drivers.drivers) {
      if (driver.membership_id === membershipId) {
        return driver.name ?? strings.payout_recipient_unnamed;
      }
    }

    return strings.payout_recipient_not_listed;
  }

  /** Вариант получателя в выборе: имя и возраст из ответа. */
  function driverCaption(driver: CompanyDriver): string {
    const ageShown = driver.age === null ? strings.drivers_not_specified : String(driver.age);

    return `${driver.name ?? strings.payout_recipient_unnamed} · ${strings.drivers_age} ${ageShown}`;
  }

  function recipientChoices(lead: Choice, current: string | null): Choice[] {
    const choices: Choice[] = [lead];
    let present = current === null;

    if (drivers.kind === 'answered') {
      for (const driver of drivers.drivers) {
        choices.push({ value: driver.membership_id, caption: driverCaption(driver) });
        present ||= driver.membership_id === current;
      }
    }

    // Текущий получатель записи стоит отдельным пунктом, даже когда его нет в
    // ответе водителей: правка суммы не должна молча менять получателя.
    if (!present && current !== null) {
      choices.push({ value: current, caption: recipientCaption(current) });
    }

    return choices;
  }

  /** Работа по идентификатору: с загруженных страниц либо из ответа `getWork`. */
  function workOf(id: string): Work | null {
    for (const known of works) {
      if (known.id === id) {
        return known;
      }
    }

    return lookedUp.get(id) ?? null;
  }

  /** Подпись работы — название и статус подписью; без ответа — нейтральная. */
  function workCaption(id: string | null): string {
    if (id === null) {
      return strings.accounting_no_work;
    }

    const found = workOf(id);

    if (found === null) {
      return `${strings.accounting_work_fallback} ${shortId(id)}`;
    }

    return `${found.title} · ${enumLabel(strings, workStatusStringKey(found.status))}`;
  }

  function workChoices(lead: Choice, current: string | null): Choice[] {
    const choices: Choice[] = [lead];
    let present = current === null;

    for (const known of works) {
      choices.push({ value: known.id, caption: workCaption(known.id) });
      present ||= known.id === current;
    }

    if (!present && current !== null) {
      choices.push({ value: current, caption: workCaption(current) });
    }

    return choices;
  }

  function currencyChoices(): Choice[] {
    const choices: Choice[] = [{ value: '', caption: strings.accounting_choose }];

    for (const value of CURRENCIES) {
      choices.push({ value, caption: enumLabel(strings, currencyStringKey(value)) });
    }

    return choices;
  }

  /** Строка списка: дата, сумма, получатель, работа, комментарий — итогов нет. */
  function row(payout: CompanyPayout): HTMLElement {
    const node = document.createElement('li');
    const line = document.createElement('span');
    const edit = document.createElement('button');
    const remove = document.createElement('button');
    const parts = [
      dateLabel(activeLocale(), payout.paid_on),
      moneyLabel(strings, payout.amount),
      recipientCaption(payout.membership_id),
      workCaption(payout.work_id),
    ];

    if (payout.comment !== null) {
      parts.push(payout.comment);
    }

    node.className = 'payout-row';
    line.textContent = parts.join(' · ');

    edit.type = 'button';
    edit.className = 'action';
    edit.textContent = strings.accounting_edit;
    edit.addEventListener('click', () => {
      openForm(payout);
    });

    remove.type = 'button';
    remove.className = 'action action--danger';
    remove.textContent = strings.accounting_delete;
    remove.disabled = deleting.has(payout.id);
    remove.addEventListener('click', () => {
      void deletePayout(payout.id);
    });

    node.append(line, edit, remove);

    return node;
  }

  function render(): void {
    fillSelect(filterRecipient, recipientChoices({ value: '', caption: strings.accounting_filter_any }, null));
    fillSelect(filterWork, workChoices({ value: '', caption: strings.accounting_filter_any }, null));

    if (form !== null) {
      const editing = form.editing;

      fillSelect(
        form.recipient,
        recipientChoices({ value: '', caption: strings.accounting_choose }, editing?.membership_id ?? null),
      );
      // Ответа водителей нет — на месте выбора текст отказа сервера (`Б14`).
      form.recipientRefusal.textContent = drivers.kind === 'refused' ? drivers.text : '';
      fillSelect(form.currency, currencyChoices());
      fillSelect(form.work, workChoices({ value: '', caption: strings.accounting_no_work }, editing?.work_id ?? null));
      form.worksMore.hidden = worksCursor === null;
      form.worksMore.disabled = worksLoading;
    }

    list.replaceChildren(...payouts.map(row));
    more.disabled = loadingMore;

    const frame: HTMLElement[] = [heading, filter, createButton, notice, formNotice];
    const chosen = chosenFilter();
    const filtered = chosen.membership_id !== undefined || chosen.work_id !== undefined;

    if (form !== null) {
      frame.push(form.form);
    }

    // Правило места состояния — одна функция на все экраны (долг `TD-66`).
    placeState(
      element,
      {
        state,
        answered,
        frame,
        list,
        after: cursor === null ? [] : [more],
        // Пустой ответ с отбором — «по этому отбору записей нет»; без отбора —
        // «выплат пока нет». Ответ пришёл в обоих случаях.
        emptyCaption: filtered ? strings.accounting_filter_empty : strings.payouts_empty,
      },
      strings,
    );
  }

  /** Отбор из выборов: пустой выбор — параметра нет вовсе. */
  function chosenFilter(): PayoutsQuery {
    const query: { membership_id?: string; work_id?: string } = {};

    if (filterRecipient.value !== '') {
      query.membership_id = filterRecipient.value;
    }

    if (filterWork.value !== '') {
      query.work_id = filterWork.value;
    }

    return query;
  }

  /** Работы строк, которых нет на загруженных страницах, — `getWork` по разу. */
  function lookUpMissingWorks(): void {
    if (!worksSettled) {
      return;
    }

    for (const payout of payouts) {
      const id = payout.work_id;

      if (id === null || workOf(id) !== null || lookedUp.has(id)) {
        continue;
      }

      lookedUp.set(id, null);
      void client
        .getWork(id)
        .then((found) => {
          lookedUp.set(id, found);
          render();
        })
        .catch(() => {
          // Отказ — нейтральная подпись «Работа» и восемь знаков; повторного
          // запроса за жизнь экрана нет.
        });
    }
  }

  /** Чтение первой страницы с текущим отбором — и первое, и всякое следующее. */
  async function readFirstPage(): Promise<void> {
    generation += 1;
    const mine = generation;

    try {
      const page = await client.listCompanyPayouts(companyId, chosenFilter());

      if (mine !== generation) {
        return;
      }

      payouts = [...page.data];
      cursor = nextPageCursor(page.meta);
      answered = true;
      state = screenStateFor({ kind: 'done', empty: page.data.length === 0 });
      lookUpMissingWorks();
    } catch (error) {
      if (mine !== generation) {
        return;
      }

      payouts = [];
      cursor = null;
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  /** Водители фирмы — подпись и выбор получателя; отказ экран не уносит. */
  async function readDrivers(): Promise<void> {
    try {
      drivers = { kind: 'answered', drivers: await client.listCompanyDrivers(companyId) };
    } catch (error) {
      drivers = { kind: 'refused', text: refusalText(error, strings) };
    }

    render();
  }

  /** Страница работ фирмы — параметром фирмы, порядок сервера (`Б10`). */
  async function readWorks(next: string | null): Promise<void> {
    worksLoading = true;
    render();

    try {
      // Фирма уходит параметром запроса: какие работы её, решает сервер.
      const query: { company_id: string; cursor?: string } = { company_id: companyId };

      if (next !== null) {
        query.cursor = next;
      }

      const page = await client.listWorks(query);

      works = [...works, ...page.data];
      worksCursor = nextPageCursor(page.meta);
    } catch (error) {
      if (form !== null) {
        formNotice.textContent = refusalText(error, strings);
      }
    } finally {
      worksLoading = false;
      worksSettled = true;
    }

    lookUpMissingWorks();
    render();
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    payouts = [];
    cursor = null;
    drivers = { kind: 'pending' };
    works = [];
    worksCursor = null;
    worksSettled = false;
    form = null;
    notice.textContent = '';
    formNotice.textContent = '';
    render();

    await Promise.all([readFirstPage(), readDrivers(), readWorks(null)]);
  }

  /** Перечитывание с первой страницы тем же отбором (`Б16`). */
  async function reread(): Promise<void> {
    await readFirstPage();
  }

  /** «Показать ещё» — та же операция, курсор и тот же отбор. */
  async function loadMore(): Promise<void> {
    const from = cursor;

    if (from === null || loadingMore) {
      return;
    }

    const mine = generation;

    loadingMore = true;
    notice.textContent = '';
    render();

    try {
      const page = await client.listCompanyPayouts(companyId, { ...chosenFilter(), cursor: from });

      if (mine === generation) {
        payouts = [...payouts, ...page.data];
        cursor = nextPageCursor(page.meta);
        lookUpMissingWorks();
      }
    } catch (error) {
      // Отказ следующей страницы не стирает прежние: текст — на месте действия.
      notice.textContent = refusalText(error, strings);
    } finally {
      loadingMore = false;
    }

    render();
  }

  /** Сервер ответил отказом — следующая отправка новая и несёт новый ключ. */
  function serverAnswered(error: unknown): boolean {
    return error instanceof ApiFailure && error.code !== null;
  }

  /** Отказ формы — под полями, которые сервер назвал; прочее — над формой. */
  function formRefused(target: HTMLFormElement, error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;
    const homeless = renderFieldMessages(target, failure?.details ?? []);
    const texts: string[] = [];

    for (const detail of homeless) {
      texts.push(detail.message);
    }

    if (texts.length === 0) {
      texts.push(refusalText(error, strings));
    }

    formNotice.textContent = texts.join(' ');
  }

  /** Тело — шесть ключей `CompanyPayoutInput`; незаполненное — `null` (`Б13`). */
  function entered(shown: PayoutForm): CompanyPayoutSubmission {
    const commentText = shown.comment.value;

    return {
      membership_id: textOf(shown.recipient.value),
      amount: textOf(shown.amount.value),
      currency: pick(CURRENCIES, shown.currency.value),
      paid_on: textOf(shown.paidOn.value),
      work_id: textOf(shown.work.value),
      comment: commentText.trim() === '' ? null : commentText,
    };
  }

  async function submitForm(shown: PayoutForm): Promise<void> {
    sending = true;
    shown.submit.disabled = true;
    formNotice.textContent = '';
    notice.textContent = '';
    renderFieldMessages(shown.form, []);

    const input = entered(shown);

    try {
      if (shown.editing === null) {
        idempotencyKey ??= newIdempotencyKey();
        await client.createCompanyPayout(companyId, input, idempotencyKey);
      } else {
        await client.updateCompanyPayout(shown.editing.id, input);
      }

      idempotencyKey = null;
      form = null;
      await reread();
    } catch (error) {
      if (serverAnswered(error)) {
        idempotencyKey = null;
      }

      formRefused(shown.form, error);
    } finally {
      sending = false;
      shown.submit.disabled = false;
    }
  }

  /** Удаление — после подтверждения; пока запрос в пути, «Удалить» неактивна. */
  async function deletePayout(payoutId: string): Promise<void> {
    if (deleting.has(payoutId) || !window.confirm(strings.payouts_delete_confirm)) {
      return;
    }

    deleting.add(payoutId);
    notice.textContent = '';
    render();

    let failure: unknown = null;

    try {
      await client.deleteCompanyPayout(payoutId);
    } catch (error) {
      failure = error;
    } finally {
      deleting.delete(payoutId);
    }

    if (failure !== null) {
      notice.textContent = refusalText(failure, strings);

      if (!serverAnswered(failure)) {
        render();

        return;
      }
    }

    await reread();
  }

  /** Форма записи (`editing` — `null`) или правки строки, что уже в памяти. */
  function buildForm(editing: CompanyPayout | null): PayoutForm {
    const shown: PayoutForm = {
      editing,
      form: document.createElement('form'),
      recipient: document.createElement('select'),
      recipientRefusal: document.createElement('span'),
      amount: document.createElement('input'),
      currency: document.createElement('select'),
      paidOn: document.createElement('input'),
      work: document.createElement('select'),
      worksMore: document.createElement('button'),
      comment: document.createElement('textarea'),
      submit: document.createElement('button'),
      close: document.createElement('button'),
      captions: {
        recipient: document.createElement('span'),
        amount: document.createElement('span'),
        currency: document.createElement('span'),
        paidOn: document.createElement('span'),
        work: document.createElement('span'),
        comment: document.createElement('span'),
      },
    };

    // Имена полей — имена полей тела контракта: по ним сервер называет место
    // своего сообщения. Пределов и обязательности у полей нет — судит сервер.
    shown.form.className = 'form payout-form';
    shown.recipient.name = 'membership_id';
    shown.recipientRefusal.className = 'form-hint';
    shown.amount.name = 'amount';
    shown.amount.type = 'text';
    shown.amount.inputMode = 'decimal';
    shown.currency.name = 'currency';
    shown.paidOn.name = 'paid_on';
    shown.paidOn.type = 'date';
    shown.work.name = 'work_id';
    shown.comment.name = 'comment';
    shown.worksMore.type = 'button';
    shown.worksMore.className = 'action';
    shown.submit.type = 'submit';
    shown.submit.className = 'action action--primary';
    shown.close.type = 'button';
    shown.close.className = 'action';

    shown.form.append(
      labelled(shown.captions.recipient, shown.recipient, shown.recipientRefusal),
      labelled(shown.captions.amount, shown.amount),
      labelled(shown.captions.currency, shown.currency),
      labelled(shown.captions.paidOn, shown.paidOn),
      labelled(shown.captions.work, shown.work),
      shown.worksMore,
      labelled(shown.captions.comment, shown.comment),
      shown.submit,
      shown.close,
    );

    // Правка любого поля — новая отправка: ключ прежней снимается (`Б5`).
    shown.form.addEventListener('input', () => {
      idempotencyKey = null;
    });
    shown.form.addEventListener('change', () => {
      idempotencyKey = null;
    });
    shown.form.addEventListener('submit', (event) => {
      event.preventDefault();

      if (sending) {
        return;
      }

      void submitForm(shown);
    });
    shown.worksMore.addEventListener('click', () => {
      if (worksLoading || worksCursor === null) {
        return;
      }

      void readWorks(worksCursor);
    });
    shown.close.addEventListener('click', () => {
      form = null;
      formNotice.textContent = '';
      render();
    });

    return shown;
  }

  /** Открыть форму: новая запись либо правка строки значениями записи. */
  function openForm(editing: CompanyPayout | null): void {
    const shown = buildForm(editing);

    form = shown;
    idempotencyKey = null;
    formNotice.textContent = '';
    caption();
    render();

    if (editing !== null) {
      shown.recipient.value = editing.membership_id;
      shown.amount.value = editing.amount.amount;
      shown.currency.value = editing.amount.currency;
      shown.paidOn.value = editing.paid_on;
      shown.work.value = editing.work_id ?? '';
      shown.comment.value = editing.comment ?? '';
    }
  }

  createButton.addEventListener('click', () => {
    openForm(null);
  });

  // Смена отбора — перечитывание с первой страницы тем же запросом сервера.
  filterRecipient.addEventListener('change', () => {
    void reread();
  });
  filterWork.addEventListener('change', () => {
    void reread();
  });
  filterReset.addEventListener('click', () => {
    filterRecipient.value = '';
    filterWork.value = '';
    void reread();
  });

  more.addEventListener('click', () => {
    void loadMore();
  });

  /** Переподписать экран на новом языке (долг `TD-72`): введённое остаётся. */
  function relabel(next: Strings): void {
    strings = next;
    caption();
    render();
  }

  caption();
  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get payouts(): readonly CompanyPayout[] {
      return payouts;
    },

    load,
    relabel,
  };
}
